import React, { useEffect, useState, useRef } from 'react';
import { getSocket } from '@/lib/socket';
import { usePlayerStore } from '@/stores/playerStore';
import { useRoomStore } from '@/stores/roomStore';

interface ProgressBarProps {
  roomId: string;
}

export const ProgressBar: React.FC<ProgressBarProps> = () => {
  const socket = getSocket();
  const { position, duration, isPlaying } = usePlayerStore();
  const { myRole } = useRoomStore();
  const [localPosition, setLocalPosition] = useState(position);
  const lastSeekTime = useRef<number>(0);

  const canSeek = myRole === 'owner' || myRole === 'dj';

  useEffect(() => {
    setLocalPosition(position);
  }, [position]);

  useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(() => {
      setLocalPosition((prev) => Math.min(prev + 1, duration));
    }, 1000);

    return () => clearInterval(interval);
  }, [isPlaying, duration]);

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!canSeek || !duration) return;

    // Prevent seek flooding - minimum 500ms between seeks
    const now = Date.now();
    if (now - lastSeekTime.current < 500) {
      console.log('Seek throttled - too soon after last seek');
      return;
    }
    lastSeekTime.current = now;

    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newPosition = Math.floor(percent * duration);

    console.log(`Emitting seek to position ${newPosition}`);
    socket.emit('sync:seek', { position: newPosition });
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? (localPosition / duration) * 100 : 0;

  return (
    <div className="w-full px-4 py-2">
      <div
        className={`relative w-full h-2 bg-muted rounded-full overflow-hidden ${
          canSeek ? 'cursor-pointer' : 'cursor-not-allowed'
        }`}
        onClick={handleSeek}
      >
        <div
          className="absolute top-0 left-0 h-full bg-gradient-to-r from-lime-400 to-lime-500 transition-all duration-300"
          style={{ width: `${progress}%` }}
        />

        {canSeek && (
          <div
            className="absolute top-1/2 transform -translate-y-1/2 w-4 h-4 bg-lime-400 rounded-full shadow-lg transition-all duration-300 hover:scale-125"
            style={{ left: `calc(${progress}% - 8px)` }}
          />
        )}
      </div>

      <div className="flex justify-between items-center mt-2 text-sm text-tertiary">
        <span>{formatTime(localPosition)}</span>
        <span>{formatTime(duration)}</span>
      </div>
    </div>
  );
};
